/**
 * Auth + Billing UI for PainToProfit.
 * Exports: AuthProvider, useAuth, AuthModal, PricingPage, AccountPage, UpgradeBanner
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, SpinnerGap, CheckCircle, Star, Crown, Lightning, SignOut, Lock } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "p2p_token";

// ---------- Axios interceptor ----------
axios.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ---------- Auth Context ----------
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setUser(null); setReady(true); return; }
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data.user);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };
  const signup = async (email, password) => {
    const r = await axios.post(`${API}/auth/signup`, { email, password });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, signup, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// ---------- Auth Modal ----------
export const AuthModal = ({ open, onClose, defaultMode = "login" }) => {
  const { login, signup } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) { setMode(defaultMode); setErr(""); setEmail(""); setPassword(""); }
  }, [open, defaultMode]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await signup(email, password);
      onClose();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="auth-modal">
      <div className="bg-white w-full max-w-md border border-zinc-200 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900">
              {mode === "login" ? "Welcome " : "Create your "}
              <span className="font-serif-italic text-violet-600">{mode === "login" ? "back" : "account"}</span>
            </h2>
            <p className="text-sm text-zinc-500 mt-1">{mode === "login" ? "Pick up where you left off." : "Free forever. No credit card."}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="auth-close-btn">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-zinc-700">Email</label>
            <input
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full px-3.5 py-3 bg-white border border-zinc-200 rounded-xl focus:border-violet-600 focus:outline-none text-sm text-zinc-900 placeholder:text-zinc-500"
              placeholder="founder@example.com"
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-700">Password</label>
            <input
              type="password" required minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full px-3.5 py-3 bg-white border border-zinc-200 rounded-xl focus:border-violet-600 focus:outline-none text-sm text-zinc-900 placeholder:text-zinc-500"
              placeholder="••••••••"
              data-testid="auth-password-input"
            />
          </div>
          {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-3.5 py-2.5" data-testid="auth-error">{err}</div>}
          <button
            type="submit" disabled={busy}
            className="w-full py-3 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-full disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_0_24px_-4px_rgba(255,107,26,0.5)] transition-all"
            data-testid="auth-submit-btn"
          >
            {busy && <SpinnerGap size={16} className="animate-spin" />}
            {mode === "login" ? "Log in →" : "Get started free →"}
          </button>
          <p className="text-center text-sm text-zinc-500">
            {mode === "login" ? "New here?" : "Already a member?"}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "signup" : "login")}
              className="text-violet-600 hover:text-violet-500 font-semibold"
              data-testid="auth-switch-mode-btn"
            >
              {mode === "login" ? "Sign up" : "Log in"}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

// ---------- Pricing Page ----------
const TIERS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    cadence: "forever",
    tagline: "Peek at the engine",
    features: [
      "Top 5 opportunities visible",
      "1 AI Deep-Dive Report (lifetime)",
      "Daily auto-scan (shared)",
      "Read-only timeline",
    ],
    cta: "Current plan",
    disabled: true,
    highlight: false,
  },
  {
    id: "founder",
    name: "Founder",
    price: "$19",
    cadence: "/month",
    tagline: "Build with conviction",
    features: [
      "Unlimited opportunities (no paywall)",
      "Unlimited AI Deep-Dive Reports",
      "Personal watchlist + niche filters",
      "CSV export",
      "Trigger manual scans",
    ],
    cta: "Start Founder",
    highlight: true,
  },
  {
    id: "pro",
    name: "Pro",
    price: "$49",
    cadence: "/month",
    tagline: "For serious operators",
    features: [
      "Everything in Founder",
      "PDF export of Deep-Dive reports",
      "Priority AI processing",
      "Backup & restore",
      "Coming soon: Daily email digest",
    ],
    cta: "Go Pro",
    highlight: false,
  },
];

export const PricingPage = ({ onRequireAuth, onClose }) => {
  const { user } = useAuth();
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  const startCheckout = async (packageId) => {
    setErr("");
    if (!user) { onRequireAuth(); return; }
    setBusyId(packageId);
    try {
      const r = await axios.post(`${API}/payments/checkout/session`, {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message || "Checkout failed");
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 sm:py-20" data-testid="pricing-page">
      <div className="text-center mb-14">
        <span className="p2p-pill-caption font-medium mb-6 inline-flex">Pricing</span>
        <h1 className="mt-6 text-5xl sm:text-7xl font-bold tracking-tight text-zinc-900 leading-[0.95]">
          Find profitable<br />
          ideas that <span className="font-serif-italic text-violet-600">scale.</span>
        </h1>
        <p className="mt-6 text-zinc-700 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
          Prime Pillar scans Reddit, Hacker News, Freelancer and Fiverr daily,<br className="hidden sm:block" />
          extracts real customer pain, and scores micro-SaaS opportunities you can ship in 5 days.
        </p>
      </div>
      {err && <div className="max-w-md mx-auto mb-6 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-3.5 py-2.5" data-testid="pricing-error">{err}</div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {TIERS.map((t) => {
          const isCurrent = user && user.tier === t.id;
          return (
            <div
              key={t.id}
              className={`relative bg-white border rounded-2xl p-7 flex flex-col transition-all hover:-translate-y-1 ${
                t.highlight
                  ? "border-violet-500 p2p-glow-violet-soft shadow-[0_24px_48px_-24px_rgba(255,107,26,0.4)]"
                  : t.id === "pro"
                  ? "border-zinc-200"
                  : "border-zinc-200"
              }`}
              data-testid={`pricing-card-${t.id}`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-violet-600 text-white text-[10px] font-semibold tracking-wider px-3 py-1 rounded-full uppercase shadow-[0_4px_16px_-2px_rgba(255,107,26,0.6)]">
                  Most popular
                </div>
              )}
              <div className="flex items-center gap-2">
                {t.id === "founder" && <Lightning size={18} weight="fill" className="text-violet-600" />}
                {t.id === "pro" && <Crown size={18} weight="fill" className="text-violet-600" />}
                <h3 className="text-2xl font-bold tracking-tight text-zinc-900">{t.name}</h3>
              </div>
              <p className="text-sm text-zinc-500 mt-1">{t.tagline}</p>
              <div className="mt-6 flex items-baseline">
                <span className="text-5xl font-bold tracking-tight text-zinc-900">{t.price}</span>
                <span className="text-zinc-500 text-sm ml-1.5">{t.cadence}</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-700">
                    <CheckCircle size={15} weight="fill" className={`${t.highlight ? "text-violet-600" : "text-emerald-400"} mt-0.5 flex-shrink-0`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                disabled={t.disabled || isCurrent || busyId === t.id}
                onClick={() => startCheckout(t.id)}
                className={`mt-7 w-full py-3 font-semibold text-sm rounded-full transition-all flex items-center justify-center gap-2 ${
                  isCurrent
                    ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/40 cursor-default"
                    : t.highlight
                    ? "bg-violet-600 hover:bg-violet-500 text-white shadow-[0_0_24px_-4px_rgba(255,107,26,0.5)]"
                    : t.id === "free"
                    ? "bg-zinc-50 text-zinc-500 border border-zinc-200 cursor-not-allowed"
                    : "bg-zinc-900 hover:bg-white text-white"
                }`}
                data-testid={`pricing-cta-${t.id}`}
              >
                {busyId === t.id && <SpinnerGap size={14} className="animate-spin" />}
                {isCurrent ? "Current plan ✓" : t.cta + " →"}
              </button>
            </div>
          );
        })}
      </div>
      <div className="text-center mt-12 text-xs text-zinc-500">
        Cancel anytime · 14-day money-back · Secure checkout via Stripe
      </div>
      {onClose && (
        <div className="text-center mt-8">
          <button onClick={onClose} className="text-sm text-zinc-500 hover:text-violet-600 transition-colors" data-testid="pricing-back-btn">
            ← Back to dashboard
          </button>
        </div>
      )}
    </div>
  );
};

// ---------- Account Page ----------
export const AccountPage = ({ onClose }) => {
  const { user, logout, refresh } = useAuth();
  const [watchlist, setWatchlist] = useState([]);
  const [niches, setNiches] = useState((user?.niches || []).join(", "));
  const [savingNiches, setSavingNiches] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paymentMsg, setPaymentMsg] = useState("");

  // Detect ?session_id=... after Stripe redirect and poll status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    setPaymentMsg("Confirming your payment…");
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const r = await axios.get(`${API}/payments/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setPaymentMsg("✅ Payment successful! Your tier has been upgraded.");
          setPolling(false);
          await refresh();
          window.history.replaceState({}, "", "/account");
          return;
        }
        if (r.data.status === "expired") {
          setPaymentMsg("⌛ Session expired. Please try again.");
          setPolling(false);
          return;
        }
        if (attempts >= 6) {
          setPaymentMsg("Still processing. Refresh in a moment to see your new tier.");
          setPolling(false);
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        setPaymentMsg("Couldn't confirm status. Please refresh.");
        setPolling(false);
      }
    };
    tick();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    axios.get(`${API}/me/watchlist`).then(r => setWatchlist(r.data.opportunities || [])).catch(() => {});
  }, []);

  const saveNiches = async () => {
    setSavingNiches(true);
    try {
      const arr = niches.split(",").map(s => s.trim()).filter(Boolean);
      await axios.put(`${API}/auth/niches`, { niches: arr });
      await refresh();
    } finally {
      setSavingNiches(false);
    }
  };

  if (!user) return null;
  const tierBadge = {
    free:    { label: "Free",    cls: "bg-zinc-50 text-zinc-700 border border-zinc-200" },
    founder: { label: "Founder", cls: "bg-violet-600/10 text-violet-600 border border-violet-300" },
    pro:     { label: "Pro",     cls: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/40" },
  }[user.tier] || { label: user.tier, cls: "bg-zinc-50 text-zinc-700 border border-zinc-200" };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 sm:py-14 space-y-6" data-testid="account-page">
      <div className="flex items-center justify-between">
        <div>
          <span className="p2p-pill-caption font-medium mb-3 inline-flex">Account</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
            Your <span className="font-serif-italic text-violet-600">workspace</span>
          </h1>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-sm text-zinc-500 hover:text-violet-600" data-testid="account-back-btn">
            ← Back
          </button>
        )}
      </div>

      {paymentMsg && (
        <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-2xl px-4 py-3 text-sm text-emerald-700 flex items-center gap-2" data-testid="payment-status-banner">
          {polling && <SpinnerGap size={14} className="animate-spin" />}
          {paymentMsg}
        </div>
      )}

      {/* Profile */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="p2p-overline mb-1.5">Email</div>
            <div className="text-zinc-900 text-sm truncate" data-testid="account-email">{user.email}</div>
          </div>
          <div>
            <div className="p2p-overline mb-1.5">Plan</div>
            <span className={`inline-block px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full ${tierBadge.cls}`} data-testid="account-tier-badge">
              {tierBadge.label}
            </span>
            {user.subscription_expires_at && (
              <div className="text-[10px] text-zinc-500 mt-1.5">
                Renews {new Date(user.subscription_expires_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <div>
            <div className="p2p-overline mb-1.5">Deep-Dives Used</div>
            <div className="text-zinc-900 font-semibold" data-testid="account-dives-used">
              {user.deep_dives_used}{user.tier === "free" && <span className="text-zinc-500"> / 1</span>}
            </div>
          </div>
          <div className="flex items-end justify-end">
            <button
              onClick={() => { logout(); if (onClose) onClose(); }}
              className="text-xs text-zinc-500 hover:text-red-400 flex items-center gap-1.5 font-medium"
              data-testid="account-logout-btn"
            >
              <SignOut size={14} /> Log out
            </button>
          </div>
        </div>
      </div>

      {/* Niche filters */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-6">
        <h2 className="text-lg font-bold tracking-tight text-zinc-900">Tune your <span className="font-serif-italic text-violet-600">signal</span></h2>
        <p className="text-sm text-zinc-500 mt-1 mb-4">Comma-separated keywords. Used to highlight opportunities matching your interest.</p>
        <div className="flex gap-2">
          <input
            value={niches}
            onChange={(e) => setNiches(e.target.value)}
            placeholder="saas, ecommerce, real estate"
            className="flex-1 px-3.5 py-2.5 bg-white border border-zinc-200 rounded-xl focus:border-violet-600 focus:outline-none text-sm text-zinc-900 placeholder:text-zinc-500"
            data-testid="niches-input"
          />
          <button
            onClick={saveNiches} disabled={savingNiches}
            className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold rounded-full disabled:opacity-50 shadow-[0_0_18px_-4px_rgba(255,107,26,0.5)]"
            data-testid="niches-save-btn"
          >
            {savingNiches ? "…" : "Save"}
          </button>
        </div>
      </div>

      {/* Watchlist */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <Star size={16} weight="fill" className="text-violet-600 drop-shadow-[0_0_4px_rgba(255,107,26,0.5)]" />
          <span className="text-xs text-zinc-500">{watchlist.length} saved</span>
        </div>
        <h2 className="text-lg font-bold tracking-tight text-zinc-900 mb-4">Your watchlist</h2>
        {watchlist.length === 0 ? (
          <p className="text-sm text-zinc-500">No starred opportunities yet. Click the star on any opportunity to save it here.</p>
        ) : (
          <ul className="divide-y divide-zinc-200" data-testid="watchlist-items">
            {watchlist.map(o => (
              <li key={o.id} className="py-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-900 truncate">{o.problem}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Score <span className="text-violet-600 font-semibold">{Math.round(o.weighted_total || 0)}</span>
                    {" · "}{o.verdict}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------- Locked teaser card / Upgrade banner ----------
export const UpgradeBanner = ({ onUpgrade, message }) => (
  <div className="relative bg-gradient-to-r from-orange-500/10 via-orange-500/5 to-transparent border border-violet-200 rounded-2xl p-5 flex items-center justify-between gap-4 overflow-hidden p2p-glow-violet-soft" data-testid="upgrade-banner">
    <div className="absolute inset-y-0 left-0 w-1 bg-violet-600" />
    <div className="relative flex items-center gap-3 min-w-0">
      <div className="w-11 h-11 bg-violet-600/15 border border-violet-300 rounded-xl flex items-center justify-center flex-shrink-0">
        <Lock size={20} weight="fill" className="text-violet-600" />
      </div>
      <div className="min-w-0">
        <div className="text-base sm:text-lg font-semibold text-zinc-900 tracking-tight">Unlock the full opportunity stream</div>
        <div className="text-xs sm:text-sm text-zinc-700 truncate">{message || "Founder & Pro plans show every scored opportunity, run unlimited Deep-Dives, and export to CSV/PDF."}</div>
      </div>
    </div>
    <button
      onClick={onUpgrade}
      className="relative bg-violet-600 hover:bg-violet-500 text-white font-semibold text-sm px-5 py-2.5 rounded-full whitespace-nowrap shadow-[0_0_24px_-4px_rgba(255,107,26,0.5)]"
      data-testid="upgrade-banner-cta"
    >
      Upgrade →
    </button>
  </div>
);
